const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface BrandPosProviderDto {
  id: string;
  brandId: string;
  type: string;
  enabled: boolean;
  syncSource: 'manual' | 'auto_sync';
  lastMenuSyncAt?: string;
  lastTimetableSyncAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type PosProviderType = 'rhino' | 'ambit' | 'shopify';

/**
 * Error carrying the backend's typed `code` (e.g. SHOPIFY_MASTER_NOT_CONNECTED) alongside the message.
 *
 * Note the code is *always* populated: the API's exception filter falls back to a generic per-status
 * code (`BAD_REQUEST`, `CONFLICT`, …) when the thrown exception declares none. So `code` being set
 * says nothing about the error being one the UI understands — match against known codes, never on
 * truthiness.
 */
export class ApiError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, opts?: { code?: string; status?: number }) {
    super(message);
    this.name = 'ApiError';
    this.code = opts?.code;
    this.status = opts?.status;
  }
}

/**
 * How the UI should present a failed POS call.
 *
 * `retryable` means nothing is wrong with what the merchant entered and the same action may well
 * succeed shortly (an in-flight sync holding the brand lock). `failure` means they have to change
 * something first. The two get different toast severities — see PosProvidersTab.
 */
export type PosErrorKind = 'retryable' | 'failure';

export function posErrorKind(err: unknown): PosErrorKind {
  return isSyncInProgressError(err) ? 'retryable' : 'failure';
}

/**
 * True when the backend refused because a POS sync for this brand is already running (HTTP 409).
 *
 * Syncs are serialized per brand with a Postgres advisory lock, so `connect` and `sync` are rejected
 * rather than queued — a second run would re-fetch the data the in-flight one is already writing. The
 * auto-sync cron holds that lock for as long as the POS takes, so a merchant pressing "Sync" during a
 * tick hits this. It's a "try again shortly", not a failure.
 *
 * Status alone is NOT enough: `POST /pos-providers/:brandId/sync/:type` answers 409 for two other
 * conditions — `POS_LOCATIONS_DEGRADED` (the POS listed no stores) and `POS_MENU_DEGRADED` (the menu
 * came back drastically smaller) — where the stored data was deliberately kept and the operator has
 * to act. Those must fall through to their own message; telling them to "retry in a moment" would
 * loop forever. The generic `CONFLICT` is the discriminator: the in-progress case throws a bare
 * `ConflictException`, so the exception filter's per-status default codes it, while both degraded
 * cases declare an `error_code` of their own.
 *
 * The message is not shown for this one: it embeds the raw brand UUID and is never translated.
 */
export function isSyncInProgressError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === 'CONFLICT';
}

/**
 * The Shopify activation gates: states the merchant has to resolve in munchies-api (the master-client
 * "coupon provider" connection) before this brand can be activated. Each maps to a
 * `pos.shopify.gate.<code>` string, except SHOPIFY_CURRENCY_MISMATCH whose backend message names the
 * two currencies and is shown verbatim.
 *
 * Kept as an explicit allowlist rather than a `SHOPIFY_*` prefix test or a truthiness check on
 * `ApiError.code`, because the filter codes *every* error — gating on anything looser would render
 * the gate panel for unrelated failures (a 409 sync-in-progress, a 404, a 500).
 */
export const SHOPIFY_GATE_CODES = [
  'SHOPIFY_MASTER_NOT_CONNECTED',
  'SHOPIFY_REAUTH_REQUIRED',
  'SHOPIFY_SCOPES_MISSING',
  'SHOPIFY_CURRENCY_MISMATCH',
] as const;

export type ShopifyGateCode = (typeof SHOPIFY_GATE_CODES)[number];

export function isShopifyGateError(err: unknown): err is ApiError & { code: ShopifyGateCode } {
  return err instanceof ApiError && SHOPIFY_GATE_CODES.includes(err.code as ShopifyGateCode);
}

export interface ValidatedProviderResult {
  locations: { externalId: string; name: string }[];
}

export interface ConnectSyncResult {
  provider: BrandPosProviderDto;
  synced: {
    locationsCount: number;
    catalogSynced: boolean;
  };
}

/** A manual "attach this modifier group to every item in this category" mapping. */
export interface CatalogModifierOverride {
  categoryName: string;
  modifierGroupName: string;
}

/** Raw POS menu structure (names only) used to configure overrides. */
export interface PosMenuStructure {
  categories: { name: string; itemCount: number }[];
  modifierGroups: { name: string; options: { name: string; priceDelta: number }[] }[];
}

/** An item whose base POS price differs from the Ambit web price. */
export interface PosPriceDiff {
  /** POS menu item id — matches the catalog item's `id`, so the UI can overlay it inline. */
  menuItemId: string;
  name: string;
  basePrice: number;
  webPrice: number;
}

async function api<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) },
  });

  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    let code: string | undefined;
    try {
      const body = await res.json();
      message = body?.message ?? message;
      // The wire field is `error_code` (ApiErrorResponseDto); `code` is only the name the backend
      // exceptions use internally and never reaches the response body.
      code = typeof body?.error_code === 'string' ? body.error_code : undefined;
    } catch {
      // ignore
    }
    throw new ApiError(message, { code, status: res.status });
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const posProvidersClient = {
  getProviders: (brandId: string) => api<BrandPosProviderDto[]>(`/pos-providers/${brandId}`),

  validate: (type: PosProviderType, credentials: Record<string, unknown>) =>
    api<ValidatedProviderResult>('/pos-providers/validate', {
      method: 'POST',
      body: JSON.stringify({ type, credentials }),
    }),

  connect: (brandId: string, type: PosProviderType, credentials: Record<string, unknown>) =>
    api<ConnectSyncResult>(`/pos-providers/${brandId}/connect`, {
      method: 'POST',
      body: JSON.stringify({ type, credentials }),
    }),

  disconnect: (id: string) => api<void>(`/pos-providers/${id}`, { method: 'DELETE' }),

  sync: (brandId: string, type: PosProviderType) =>
    api<void>(`/pos-providers/${brandId}/sync/${type}`, { method: 'POST' }),

  /** Raw POS menu structure (category + modifier-group names) for configuring overrides. */
  getMenuStructure: (brandId: string) => api<PosMenuStructure>(`/pos-providers/${brandId}/menu-structure`),

  /** Items whose base POS price differs from the Ambit web price, for one location. */
  getPriceComparison: (brandId: string, locationId: string) =>
    api<PosPriceDiff[]>(`/pos-providers/${brandId}/locations/${locationId}/price-comparison`),

  getModifierOverrides: (brandId: string) =>
    api<CatalogModifierOverride[]>(`/pos-providers/${brandId}/modifier-overrides`),

  setModifierOverrides: (brandId: string, overrides: CatalogModifierOverride[]) =>
    api<CatalogModifierOverride[]>(`/pos-providers/${brandId}/modifier-overrides`, {
      method: 'PUT',
      body: JSON.stringify({ overrides }),
    }),
};
